import React from 'react';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Divider } from 'primereact/divider';
import { CoursePollWithVotesResponse } from '../../../../../api/types/students/course-poll-feedbacks/course-poll-with-questions';
import { CourseFeedbackPoll } from '../../../../../api/types/programs/course/feedback/course-feedback-poll';
import { PollQuestionType } from '../../../../../api/types/settings/settings-all';
import { PollQuestionCard } from './compoenents/poll-question-card';

export interface PollCardProps {
  poll: CoursePollWithVotesResponse;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onDelete: (feedback: CourseFeedbackPoll) => void;
}

// Helper function to calculate average for a poll
const calculatePollAverage = (
  poll: CoursePollWithVotesResponse,
): number | null => {
  // Calculate average for each multiple choice question
  const questionAverages = poll.questions
    .map((question) => {
      if (question.questionType !== PollQuestionType.multiple_choice) {
        return null;
      }
      if (question.totalResponses === 0) {
        return null;
      }
      const totalValue = question.options.reduce(
        (sum, option) => sum + option.optionValue * option.voteCount,
        0,
      );
      return totalValue / question.totalResponses;
    })
    .filter((avg): avg is number => avg !== null);

  if (questionAverages.length === 0) {
    return null; // No questions with numeric averages
  }

  // Average of all question averages
  const sum = questionAverages.reduce((acc, avg) => acc + avg, 0);
  return sum / questionAverages.length;
};

const formatAverage = (average: number | null): string => {
  if (average === null) {
    return 'אין נתונים';
  }
  return average.toFixed(2);
};

export const PollCard: React.FC<PollCardProps> = ({
  poll,
  isExpanded,
  onToggleExpand,
  onDelete,
}) => {
  const pollAverage = calculatePollAverage(poll);

  return (
    <Card
      style={{
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      }}
    >
      <div style={{ direction: 'rtl' }}>
        {/* Poll Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '12px',
          }}
        >
          <div style={{ flex: 1 }}>
            <h4
              style={{
                margin: '0 0 8px 0',
                color: '#1f2937',
                fontSize: '18px',
              }}
            >
              {poll.pollName}
            </h4>
            <div
              style={{
                display: 'flex',
                gap: '16px',
                marginTop: '16px',
                flexWrap: 'wrap',
                fontSize: '14px',
                color: '#6b7280',
              }}
            >
              <span style={{ fontSize: '16px' }}>
                מרצה:
                <strong style={{ fontSize: '16px' }}>
                  {'  '}
                  {poll.professorFirstName} {poll.professorLastName}
                </strong>
              </span>
              <span style={{ fontSize: '16px' }}>
                {/*<strong style={{ fontSize: '16px' }}></strong>{' '}*/}
                ציון ממוצע:
                <span
                  style={{
                    color: '#059669',
                    fontWeight: 'bold',
                    fontSize: '16px',
                  }}
                >
                  {'  '}
                  {formatAverage(pollAverage)}
                </span>
              </span>
              <span style={{ fontSize: '16px' }}>
                תגובות:
                <strong style={{ fontSize: '16px' }}>
                  {'  '}
                  {poll.totalSubmissions} / {poll.totalEnrolled}
                </strong>{' '}
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button
              icon={isExpanded ? 'pi pi-chevron-up' : 'pi pi-chevron-down'}
              className="p-button-text p-button-sm"
              onClick={onToggleExpand}
              label={isExpanded ? 'הסתר' : 'הצג פרטים'}
            />
            <Button
              icon="pi pi-trash"
              className="p-button-text p-button-sm p-button-danger"
              onClick={() => {
                // Convert CoursePollWithVotesResponse to CourseFeedbackPoll for delete dialog
                onDelete({
                  id: poll.programCoursePollId,
                  courseId: poll.courseId,
                  settingsAbstractPollId: poll.id,
                  pollName: poll.pollName,
                  isActive: true, // We don't have this info, using default
                  isAnonymous: poll.isAnonymous,
                  blockGradeView: poll.blockGradeView,
                  createdAt: new Date(), // We don't have this info, using default
                  createdBy: 0, // We don't have this info, using default
                  createdByName: '', // We don't have this info, using default
                });
              }}
            />
          </div>
        </div>

        {/* Expanded Questions */}
        {isExpanded && (
          <div style={{ marginTop: '16px' }}>
            <Divider />
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '20px',
                marginTop: '16px',
              }}
            >
              {poll.questions.map((question) => (
                <PollQuestionCard key={question.id} question={question} />
              ))}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};
